import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Paper, 
  Typography,
  Grid,
  Alert,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import toast from 'react-hot-toast';
import { appLogger } from '../../../utils/errorLogger';
import * as utilityApi from '../../../api/utilityServices';
import { UtilityService, CreateUtilityServiceDTO } from '../../../types/utility.types';
import {
  EnergyForm,
  WaterForm,
  WasteForm,
  TelecomsForm,
  OtherForm,
} from '../../../components/ContractForms';
import CommissionPaymentManager from '../../../components/CommissionPayments/CommissionPaymentManager';
import { useContractData } from './hooks/useContractData';
import { useContractForm } from './hooks/useContractForm';
import { ClientPropertySection } from './components/ClientPropertySection';
import { ContractDetailsSection } from './components/ContractDetailsSection';
import { SubBrokerSection } from './components/SubBrokerSection';

const ContractForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const clientIdFromQuery = queryParams.get('clientId');
  const propertyIdFromQuery = queryParams.get('propertyId');

  // Get contract ID from URL params
  const { id } = useParams<{ id?: string }>();
  const [contractId] = useState<string>(id || '');

  // Determine if we're in edit mode or view mode
  const pathIncludesEdit = location.pathname.includes('/edit');
  const [isEditMode, setIsEditMode] = useState<boolean>(pathIncludesEdit);
  const [isViewMode, setIsViewMode] = useState<boolean>(!!id && !pathIncludesEdit);
  
  // Update mode when path changes
  useEffect(() => {
    const editMode = location.pathname.includes('/edit');
    const viewMode = !!id && !editMode;
    
    setIsEditMode(editMode);
    setIsViewMode(viewMode);
    
    console.log('Form Mode Updated:', { id, editMode, viewMode, pathname: location.pathname });
  }, [location.pathname, id]);

  // Use custom hooks
  const {
    clients,
    properties,
    suppliers,
    serviceTypes,
    statuses,
    selectedClient,
    loading: dataLoading,
    error: dataError,
    loadClient,
    loadProperties,
  } = useContractData();

  const {
    formData,
    setFormData,
    handleFormChange,
    handleNestedChange,
  } = useContractForm(clientIdFromQuery || undefined, propertyIdFromQuery || undefined);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Load properties when client changes (for new contracts)
  useEffect(() => {
    if (formData.clientId && !id) {
      console.log('Loading properties for selected client:', formData.clientId);
      loadProperties(formData.clientId);
    }
  }, [formData.clientId, id, loadProperties]);

  // Load client and properties when coming from Client Details with query params
  useEffect(() => {
    if (clientIdFromQuery && !id) {
      console.log('Loading client and properties from query params:', clientIdFromQuery);
      loadClient(clientIdFromQuery);
      loadProperties(clientIdFromQuery);
    }
  }, [clientIdFromQuery, id, loadClient, loadProperties]);

  // Load client details when client is selected in the form
  useEffect(() => {
    if (formData.clientId && formData.clientId !== clientIdFromQuery && !selectedClient) {
      console.log('Loading client from form selection:', formData.clientId);
      loadClient(formData.clientId);
      loadProperties(formData.clientId);
    }
  }, [formData.clientId, clientIdFromQuery, selectedClient, loadClient, loadProperties]);

  // Load contract data if editing/viewing
  useEffect(() => {
    if (!contractId) return;

    const loadContract = async () => {
      try {
        setLoading(true);
        const contract = await utilityApi.getUtilityService(contractId);
        
        if (!contract) {
          setError('Contract not found');
          return;
        }
        
        // Populate form with contract data
        setFormData({
          serviceType: contract.serviceType || '',
          accountNumber: contract.accountNumber || '',
          status: contract.status || '',
          notes: contract.notes || '',
          supplier: typeof contract.supplier === 'string' ? contract.supplier : contract.supplier?._id || '',
          meterDetails: {
            meterType: contract.meterDetails?.meterType || '',
            location: contract.meterDetails?.location || '',
            serialNumber: contract.meterDetails?.serialNumber || '',
            mpanMprnSpid: contract.meterDetails?.mpanMprnSpid || ''
          },
          subServices: Array.isArray(contract.subServices) ? contract.subServices.map(s => 
            typeof s === 'string' ? s : (s as any)._id
          ) : [],
          contractStartDate: contract.contractStartDate ? new Date(contract.contractStartDate) : null,
          contractEndDate: contract.contractEndDate ? new Date(contract.contractEndDate) : null,
          currentRate: contract.currentRate || '',
          eacAq: contract.eacAq || '',
          clientId: clientIdFromQuery || (typeof contract.client === 'string' ? contract.client : contract.client?._id),
          propertyId: propertyIdFromQuery || (typeof contract.property === 'string' ? contract.property : contract.property?._id),
          standingCharge: typeof contract.standingCharge === 'number' ? contract.standingCharge : undefined,
          commissionUplift: contract.commissionUplift || '',
          contractValue: contract.contractValue || '',
          annualUsage: contract.annualUsage || '',
          dayRate: typeof contract.dayRate === 'number' ? contract.dayRate : undefined,
          dayConsumption: typeof contract.dayConsumption === 'number' ? contract.dayConsumption : undefined,
          nightRate: typeof contract.nightRate === 'number' ? contract.nightRate : undefined,
          nightConsumption: typeof contract.nightConsumption === 'number' ? contract.nightConsumption : undefined,
          weekendRate: typeof contract.weekendRate === 'number' ? contract.weekendRate : undefined,
          weekendConsumption: typeof contract.weekendConsumption === 'number' ? contract.weekendConsumption : undefined,
          uplift: typeof contract.uplift === 'number' ? contract.uplift : undefined,
          subBroker: contract.subBroker,
        });
        
        const contractClientId = typeof contract.client === 'string' ? contract.client : contract.client?._id;
        
        if (contractClientId) {
          await loadClient(contractClientId);
          await loadProperties(contractClientId);
        }
      } catch (err: any) {
        console.error('Failed to load contract:', err);
        if (err.response && err.response.status === 404) {
          setError(`Contract with ID ${contractId} not found.`);
        } else {
          setError(`Failed to load contract: ${err.message || 'Unknown error'}`);
        }
      } finally {
        setLoading(false);
      }
    };

    loadContract();
  }, [contractId, clientIdFromQuery, propertyIdFromQuery, loadClient, loadProperties, setFormData]);

  // Render the appropriate form based on the service type
  const renderServiceTypeForm = () => {
    const utilityFormData = formData as unknown as UtilityService;
    
    const commonProps = {
      formData: utilityFormData,
      handleFormChange,
      handleNestedChange,
      isViewMode,
    };

    const serviceType = formData.serviceType?.toUpperCase();
    
    const isGasOrElectricity = serviceType?.includes('GAS') || serviceType?.includes('ELECTRIC');
    const isWater = serviceType?.includes('WATER');
    const isWaste = serviceType?.includes('WASTE');
    const isTelecoms = serviceType?.includes('TELECOM');
    
    if (isGasOrElectricity) {
      return <EnergyForm {...commonProps} />;
    } else if (isWater) {
      return <WaterForm {...commonProps} />;
    } else if (isWaste) {
      return <WasteForm {...commonProps} />;
    } else if (isTelecoms) {
      return <TelecomsForm {...commonProps} />;
    } else {
      return <OtherForm {...commonProps} />;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const errors: string[] = [];
    if (!formData.clientId) errors.push('Client is required');
    if (!formData.propertyId) errors.push('Property is required');
    if (!formData.serviceType) errors.push('Service Type is required');
    if (!formData.status) errors.push('Status is required');
    if (!formData.supplier) errors.push('Supplier is required');
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      toast.error('Please fill in all required fields');
      return;
    }
    
    setValidationErrors([]);
    setLoading(true);
    setError(null);
    
    try {
      // Prepare data for submission
      const submissionData: any = {
        serviceType: formData.serviceType,
        accountNumber: formData.accountNumber,
        status: formData.status,
        supplier: formData.supplier,
        notes: formData.notes,
        meterDetails: formData.meterDetails,
        subServices: formData.subServices,
        contractStartDate: formData.contractStartDate?.toISOString(),
        contractEndDate: formData.contractEndDate?.toISOString(),
        currentRate: formData.currentRate,
        eacAq: formData.eacAq,
        standingCharge: formData.standingCharge,
        commissionUplift: formData.commissionUplift,
        contractValue: formData.contractValue,
        annualUsage: formData.annualUsage,
        dayRate: formData.dayRate,
        dayConsumption: formData.dayConsumption,
        nightRate: formData.nightRate,
        nightConsumption: formData.nightConsumption,
        weekendRate: formData.weekendRate,
        weekendConsumption: formData.weekendConsumption,
        uplift: formData.uplift,
        subBroker: formData.subBroker,
      };
      
      // Remove undefined/null values
      Object.keys(submissionData).forEach(key => {
        if (submissionData[key] === undefined || submissionData[key] === null || submissionData[key] === '') {
          delete submissionData[key];
        }
      });
      
      if (isEditMode && contractId) {
        // Update existing contract
        await utilityApi.updateUtilityService(contractId, submissionData);
        toast.success('Contract updated successfully');
        navigate(`/utilities/contracts/${contractId}?propertyId=${formData.propertyId}&clientId=${formData.clientId}`);
      } else {
        // Create new contract
        const newContract = await utilityApi.createUtilityService(
          formData.clientId!,
          formData.propertyId!,
          submissionData as CreateUtilityServiceDTO
        );
        toast.success('Contract created successfully');
        navigate(`/utilities/contracts/${newContract._id}?propertyId=${formData.propertyId}&clientId=${formData.clientId}`);
      }
    } catch (err: any) {
      console.error('Failed to save contract:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save contract';
      setError(errorMessage);
      toast.error(errorMessage);
      appLogger.error(err instanceof Error ? err : new Error('Failed to save contract'), {
        metadata: { 
          contractId,
          isEditMode,
          formData 
        },
        source: 'ContractForm.handleSubmit'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading || dataLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3,
            backgroundColor: 'rgba(25, 25, 25, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4">
              {isViewMode ? 'View Contract' : isEditMode ? 'Edit Contract' : 'New Contract'}
            </Typography>
            {isViewMode && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setIsViewMode(false);
                  setIsEditMode(true);
                  navigate(`/utilities/contracts/${contractId}/edit?propertyId=${formData.propertyId}&clientId=${formData.clientId}`, { replace: true });
                }}
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                }}
              >
                Edit Contract
              </Button>
            )}
          </Box>

          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle2">Please fix the following errors:</Typography>
              <ul>
                {validationErrors.map((err, index) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            </Alert>
          )}

          {(error || dataError) && (
            <Alert severity="error" sx={{ mb: 3 }}>{error || dataError}</Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Client & Property Section */}
              <ClientPropertySection
                clients={clients}
                properties={properties}
                selectedClientId={formData.clientId}
                selectedPropertyId={formData.propertyId}
                onClientChange={(clientId) => handleFormChange('clientId', clientId)}
                onPropertyChange={(propertyId) => handleFormChange('propertyId', propertyId)}
                clientIdFromQuery={clientIdFromQuery || undefined}
                propertyIdFromQuery={propertyIdFromQuery || undefined}
                isViewMode={isViewMode}
              />

              {/* Contract Details Section */}
              <ContractDetailsSection
                serviceType={formData.serviceType}
                status={formData.status}
                supplier={formData.supplier}
                accountNumber={formData.accountNumber}
                contractStartDate={formData.contractStartDate}
                contractEndDate={formData.contractEndDate}
                serviceTypes={serviceTypes}
                statuses={statuses}
                suppliers={suppliers}
                onFieldChange={handleFormChange}
                isViewMode={isViewMode}
              />

              {/* Sub-Broker Attribution Section */}
              <SubBrokerSection
                clientRepresentativeId={
                  selectedClient?.representativeId 
                    ? typeof selectedClient.representativeId === 'string' 
                      ? selectedClient.representativeId 
                      : (selectedClient.representativeId as any)?._id || String(selectedClient.representativeId)
                    : undefined
                }
                subBroker={formData.subBroker}
                onSubBrokerChange={(subBroker) => handleFormChange('subBroker', subBroker)}
                isViewMode={isViewMode}
                isEditMode={isEditMode}
              />

              {/* Service-Specific Form */}
              {formData.serviceType && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Service-Specific Details</Typography>
                  <Box sx={{ p: 1 }}>
                    {renderServiceTypeForm()}
                  </Box>
                </Grid>
              )}

              {/* Commission Payments - Only for existing contracts */}
              {contractId && formData.contractValue && (
                <Grid item xs={12}>
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 2, 
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      Commission Tracking
                    </Typography>
                    <CommissionPaymentManager 
                      contractId={contractId}
                      contractValue={formData.contractValue ? parseFloat(formData.contractValue.toString()) : 0}
                      onUpdate={() => {/* reload contract */}}
                    />
                  </Paper>
                </Grid>
              )}

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/utilities/contracts')}
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)'
                      }
                    }}
                  >
                    {isViewMode ? 'Back to Contracts' : 'Cancel'}
                  </Button>
                  {!isViewMode && (
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{
                        backgroundColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        backdropFilter: 'blur(4px)',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      {isEditMode ? 'Update Contract' : 'Create Contract'}
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default ContractForm;
